import { EmbedInteraction, EmbedStatus, EmbedSubmissionType, ActivityResponse, ActivityResponsePayload, Submission, SlideSubmissionFeedback } from '../types';
import { type Slide } from '../components/course-builder/slides/slideTypes';
import { supabase } from '../lib/supabase';
import { reportError } from '../lib/errorHandling';
import {
  latestCheckpoints,
  checkpointCorrectness,
  type EmbedResult,
  type EmbedCheckpoint,
  type EmbedState,
  type EmbedCheckpointRow,
} from '../lib/embedProtocol';
import { findRegistryBySlide } from '../components/course-builder/slides/slideRegistry';
import {
  writeInteraction,
  scoreResponse,
  isLearningInteractionsEnabled,
  type WriteInteractionInput,
  type WriteInteractionResult,
} from './learningInteractionService';

/**
 * Student activity tracking: embed interactions, activity responses, and
 * submissions. Split out of courseService (June 2026); courseService spreads
 * these methods for backward compatibility, but new code should import
 * activityService directly.
 */
export const activityService = {
  // --- EMBED INTERACTION TRACKING ---

  /**
   * Track an interaction with an embedded webapp
   * Uses the database function for upsert to handle race conditions
   */
  async trackEmbedInteraction(
    courseId: string,
    lessonId: string,
    slideId: string,
    embedUrl: string,
    interaction: {
      status?: EmbedStatus;
      progress?: number;
      score?: number;
      timeSpentSeconds?: number;
      submissionData?: unknown;
      submissionType?: EmbedSubmissionType;
    }
  ): Promise<EmbedInteraction> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.rpc('upsert_embed_interaction', {
      p_course_id: courseId,
      p_lesson_id: lessonId,
      p_slide_id: slideId,
      p_embed_url: embedUrl,
      p_status: interaction.status || null,
      p_progress: interaction.progress ?? null,
      p_score: interaction.score ?? null,
      p_time_spent_seconds: interaction.timeSpentSeconds ?? null,
      p_submission_data: interaction.submissionData ? JSON.stringify(interaction.submissionData) : null,
      p_submission_type: interaction.submissionType || null
    });

    if (error) throw new Error(error.message);

    return mapEmbedInteraction(data);
  },

  /**
   * Record one run-through's marks from an embedded app (embed_results).
   * Append-only and idempotent on the result's attemptId — unlike
   * trackEmbedInteraction, which upserts and keeps only the latest attempt.
   * The RPC resolves the caller's own enrollment; no user id is sent.
   */
  async recordEmbedResult(
    courseId: string,
    lessonId: string,
    slideId: string,
    embedUrl: string,
    result: EmbedResult,
  ): Promise<string> {
    if (!result.attemptId) throw new Error('recordEmbedResult: attemptId is required');
    const { data, error } = await supabase.rpc('record_embed_result', {
      p_course_id: courseId,
      p_lesson_id: lessonId,
      p_slide_id: slideId,
      p_embed_url: embedUrl || null,
      p_attempt_id: result.attemptId,
      p_earned: result.earned,
      p_possible: result.possible,
      // An array, not a JSON string: the client encodes rpc args itself.
      p_items: result.items,
    });
    if (error) throw new Error(error.message);
    return data as string;
  },

  /**
   * Record one checkpoint from an embedded app as a learning_interactions row
   * (template 'webapp', activity_key = the step), which is where a native
   * revise-run records its own items, so reporting and resume read embed and
   * native data alike. Upserted on (enrollment, slide:attempt:step): a step
   * re-sent within an attempt overwrites, because a learner revising an answer
   * is the normal case, not a second attempt. Best-effort like every
   * dual-write; the skip reason comes back rather than an exception. Not
   * enrolled (staff previewing) is a skip, not an error.
   */
  async recordEmbedCheckpoint(
    courseId: string,
    lessonId: string,
    slide: Slide,
    embedUrl: string,
    checkpoint: EmbedCheckpoint,
  ): Promise<WriteInteractionResult> {
    if (!checkpoint.attemptId) throw new Error('recordEmbedCheckpoint: attemptId is required');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { written: false, skipReason: 'not-authenticated' };
    const { data: enrollment, error: enrollmentError } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();
    if (enrollmentError) throw new Error(enrollmentError.message);
    if (!enrollment) return { written: false, skipReason: 'not-enrolled' };

    const { step, attemptId, result, data } = checkpoint;
    return writeInteraction({
      enrollmentId: enrollment.id,
      lessonId,
      slide,
      activityKey: step,
      verb: result ? 'submitted' : 'responded',
      response: { step, attemptId, result, data, embedUrl: embedUrl || null },
      correctness: checkpointCorrectness(result),
      score: result ? result.earned / result.possible : null,
      // Slide id included: two apps in one lesson may both call a step "argue"
      // in an attempt both call "1".
      clientId: `${slide.id}:${attemptId}:${step}`,
    });
  },

  /**
   * What the lesson already knows about this learner on this LESSON, for the
   * `state` field of lms-init: the latest checkpoint per (slide, step) across
   * every webapp slide in the lesson, and every completed run-through's marks.
   * Null when there is nothing to know (not signed in, not enrolled): the app
   * must treat that like an empty history. Reads only the caller's own rows.
   */
  async getEmbedState(courseId: string, lessonId: string): Promise<EmbedState | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: enrollment } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();
    if (!enrollment) return null;

    const state: EmbedState = { checkpoints: [], results: [] };

    if (await isLearningInteractionsEnabled()) {
      // Newest first, bounded: 500 rows covers a lesson of several apps with
      // tens of steps each and a few attempts; beyond that the oldest rows are
      // superseded anyway.
      const { data, error } = await supabase
        .from('learning_interactions')
        .select('slide_id, activity_key, response, server_ts')
        .eq('enrollment_id', enrollment.id)
        .eq('lesson_id', lessonId)
        .eq('template', 'webapp')
        .order('server_ts', { ascending: false })
        .limit(500);
      if (error) throw new Error(error.message);
      state.checkpoints = latestCheckpoints((data ?? []) as EmbedCheckpointRow[]);
    }

    const { data: results, error: rErr } = await supabase
      .from('embed_results')
      .select('slide_id, attempt_id, earned, possible, items, created_at')
      .eq('enrollment_id', enrollment.id)
      .eq('lesson_id', lessonId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (rErr) throw new Error(rErr.message);
    state.results = (results ?? []).map((r) => ({
      slideId: r.slide_id as string,
      attemptId: r.attempt_id as string,
      earned: Number(r.earned),
      possible: Number(r.possible),
      items: (r.items as EmbedState['results'][number]['items']) ?? null,
      recordedAt: r.created_at as string,
    }));
    return state;
  },

  /**
   * Get all embed interactions for a specific lesson
   */
  async getEmbedInteractions(courseId: string, lessonId: string): Promise<EmbedInteraction[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // First get the enrollment
    const { data: enrollment, error: enrollmentError } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();

    if (enrollmentError) throw new Error(enrollmentError.message);
    if (!enrollment) return []; // Not enrolled

    const { data, error } = await supabase
      .from('embed_interactions')
      .select('*')
      .eq('enrollment_id', enrollment.id)
      .eq('lesson_id', lessonId);

    if (error) throw new Error(error.message);

    return (data || []).map(mapEmbedInteraction);
  },

  /**
   * Get a specific embed interaction by slide ID
   */
  async getEmbedInteractionBySlide(courseId: string, lessonId: string, slideId: string): Promise<EmbedInteraction | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // First get the enrollment
    const { data: enrollment, error: enrollmentError } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();

    if (enrollmentError) throw new Error(enrollmentError.message);
    if (!enrollment) return null; // Not enrolled

    const { data, error } = await supabase
      .from('embed_interactions')
      .select('*')
      .eq('enrollment_id', enrollment.id)
      .eq('lesson_id', lessonId)
      .eq('slide_id', slideId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw new Error(error.message);
    }

    return mapEmbedInteraction(data);
  },

  // --- ACTIVITY RESPONSE TRACKING ---

  async trackActivityResponse(
    courseId: string,
    lessonId: string,
    slideId: string,
    response: ActivityResponsePayload,
    slide?: Slide,
    /**
     * Phase 9 §9c — caller-resolved experience id for this slide (null if the
     * slide isn't in a scenario group, or the lesson hasn't been backfilled,
     * or fk_experiences_table is off). Stamped onto the learning_interactions
     * row so the gradebook can roll up by experience later.
     */
    experienceId?: string | null,
  ): Promise<ActivityResponse> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.rpc('upsert_activity_response', {
      p_course_id: courseId,
      p_lesson_id: lessonId,
      p_slide_id: slideId,
      p_activity_type: response.activityType,
      p_mode: response.mode,
      p_response_data: JSON.stringify(response.responseData),
      p_is_correct: response.isCorrect ?? null,
      p_score: response.score ?? null,
    });

    if (error) throw new Error(error.message);

    // Dual-write to learning_interactions when the slide is provided and the
    // tenant flag is on (the service checks the flag itself; this is best-effort
    // and must never block the primary write path).
    if (slide) {
      void dualWriteLearningInteractions(
        data.enrollment_id,
        lessonId,
        slide,
        response,
        experienceId ?? null,
      );
    }

    return mapActivityResponse(data);
  },

  async getActivityResponses(courseId: string, lessonId: string): Promise<ActivityResponse[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: enrollment, error: enrollmentError } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();

    if (enrollmentError) throw new Error(enrollmentError.message);
    if (!enrollment) return []; // Not enrolled

    const { data, error } = await supabase
      .from('activity_responses')
      .select('*')
      .eq('enrollment_id', enrollment.id)
      .eq('lesson_id', lessonId);

    if (error) throw new Error(error.message);

    return (data || []).map(mapActivityResponse);
  },

  async getActivityResponseBySlide(courseId: string, lessonId: string, slideId: string): Promise<ActivityResponse | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: enrollment, error: enrollmentError } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();

    if (enrollmentError) throw new Error(enrollmentError.message);
    if (!enrollment) return null; // Not enrolled

    const { data, error } = await supabase
      .from('activity_responses')
      .select('*')
      .eq('enrollment_id', enrollment.id)
      .eq('lesson_id', lessonId)
      .eq('slide_id', slideId)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }

    return mapActivityResponse(data);
  },

  // --- SUBMISSIONS ---

  async createSubmission(
    courseId: string,
    lessonId: string,
    slideId: string,
    submissionType: string,
    submissionData: unknown,
    activityResponseId?: string,
    embedInteractionId?: string
  ): Promise<Submission> {
    const { data, error } = await supabase.rpc('create_submission', {
      p_course_id: courseId,
      p_lesson_id: lessonId,
      p_slide_id: slideId,
      p_submission_type: submissionType,
      p_submission_data: JSON.stringify(submissionData),
      p_activity_response_id: activityResponseId || null,
      p_embed_interaction_id: embedInteractionId || null,
    });

    if (error) throw new Error(error.message);
    return mapSubmission(data);
  },

  async getLessonSubmissionFeedback(
    courseId: string,
    lessonId: string
  ): Promise<SlideSubmissionFeedback[]> {
    const { data, error } = await supabase.rpc('get_lesson_submission_feedback', {
      p_course_id: courseId,
      p_lesson_id: lessonId,
    });

    if (error) throw new Error(error.message);
    return (data || []).map((r: any) => ({
      slideId: r.slide_id,
      score: r.score,
      feedback: r.feedback,
      status: r.status,
      gradedAt: r.graded_at ? new Date(r.graded_at) : null,
    }));
  }
};

// Helper function to map database row to ActivityResponse type
function mapActivityResponse(row: any): ActivityResponse {
  return {
    id: row.id,
    enrollmentId: row.enrollment_id,
    lessonId: row.lesson_id,
    slideId: row.slide_id,
    activityType: row.activity_type,
    mode: row.mode,
    responseData: row.response_data,
    isCorrect: row.is_correct ?? undefined,
    score: row.score ?? undefined,
    submittedAt: new Date(row.submitted_at),
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

function mapSubmission(row: any): Submission {
  return {
    id: row.id,
    enrollmentId: row.enrollment_id,
    lessonId: row.lesson_id,
    slideId: row.slide_id,
    activityResponseId: row.activity_response_id || undefined,
    embedInteractionId: row.embed_interaction_id || undefined,
    submissionType: row.submission_type,
    submissionData: row.submission_data,
    score: row.score,
    feedback: row.feedback,
    status: row.status,
    gradedBy: row.graded_by || undefined,
    gradedAt: row.graded_at ? new Date(row.graded_at) : null,
    submittedAt: new Date(row.submitted_at),
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

// Helper function to map database row to EmbedInteraction type
function mapEmbedInteraction(row: any): EmbedInteraction {
  return {
    id: row.id,
    enrollmentId: row.enrollment_id,
    lessonId: row.lesson_id,
    slideId: row.slide_id,
    embedUrl: row.embed_url,
    status: row.status,
    progress: row.progress || 0,
    score: row.score ?? undefined,
    timeSpentSeconds: row.time_spent_seconds || 0,
    submissionData: row.submission_data ?? undefined,
    submissionType: row.submission_type ?? undefined,
    startedAt: row.started_at ? new Date(row.started_at) : undefined,
    completedAt: row.completed_at ? new Date(row.completed_at) : undefined,
    lastInteractionAt: new Date(row.last_interaction_at)
  };
}

/**
 * Dual-write a single activity response into learning_interactions. Best-effort
 * and silent — failures are logged but never surfaced. Reflection slides produce
 * one row per question (activityKeyExtractor); all others produce one row keyed
 * 'q'. The framework's Phase 0 ships dual-write only at the terminal verb
 * ('submitted' for graded, 'responded' for exploration); finer-grained verbs
 * (started, revised, abandoned) land in later phases.
 */
async function dualWriteLearningInteractions(
  enrollmentId: string,
  lessonId: string,
  slide: Slide,
  response: ActivityResponsePayload,
  /** Phase 9 §9c — threaded through to every per-fan-out row write. */
  experienceId: string | null,
): Promise<void> {
  try {
    const entry = findRegistryBySlide(slide);
    const meta = entry?.interaction;
    if (!meta) return;

    const tier =
      typeof meta.tier === 'function' ? meta.tier(slide.data) : meta.tier;
    const verb = tier === 'graded' ? 'submitted' : 'responded';
    const responseData = response.responseData as Record<string, unknown>;

    // Three write shapes:
    //  1. Per-row payload (reveal-predict, perspective-lenses): the response
    //     already carries one sub-activity's data. Write ONE row, key derived
    //     from the payload via responseToActivityKey.
    //  2. Batched payload with activityKeyExtractor (reflection): one call =
    //     all sub-activities. Fan out — one row per key — and project to the
    //     per-row schema (`{ questionId, text }` for now; future templates
    //     that also batch will need their own projection).
    //  3. Plain payload, no extractor (quiz, single-choice activity types):
    //     one row, key = 'q'.
    const selfKey = meta.responseToActivityKey?.(responseData) ?? null;

    type Write = { key: string; payload: Record<string, unknown> };
    let writes: Write[];
    if (selfKey) {
      writes = [{ key: selfKey, payload: responseData }];
    } else if (meta.activityKeyExtractor) {
      writes = meta
        .activityKeyExtractor(slide.data)
        .map((key) => ({
          key,
          payload: { questionId: key, text: (responseData[key] as string | undefined) ?? '' },
        }));
    } else {
      writes = [{ key: 'q', payload: responseData }];
    }

    for (const { key, payload } of writes) {
      const scored = scoreResponse(meta, slide.data, key, payload);
      const input: WriteInteractionInput = {
        enrollmentId,
        lessonId,
        slide,
        activityKey: key,
        verb,
        response: payload,
        correctness: scored.correctness,
        score: scored.score,
        clientId: crypto.randomUUID(),
        experienceId,
      };
      const result = await writeInteraction(input);
      if (!result.written && result.skipReason && result.skipReason !== 'flag-off') {
        console.debug('learning_interactions skipped:', result.skipReason);
      }
    }
  } catch (e) {
    // Best-effort: analytics dual-write must not block the primary response save.
    reportError('activityService.learningInteractionDualWrite', e, { lessonId });
  }
}
